import React, { useEffect, useState } from 'react';
import { Button, Catalogue, CatalogueItem, IFeesService, Modal } from 'src/features/common';
import { feeSliderDevices } from '../../helpers/fee-slider-devices';
import { useMultiStep } from 'src/hooks/use-multi-step';
import { DefaultButtonStyle } from 'src/features/common/components/button/revamp-button';
import { ButtonParamType } from 'src/features/common/components/button/revamp-button';
import { IDevice } from 'src/features/common/types/fees-slider-types';
import ChooseYourDevice from '../choose-your-device/choose-your-device';
import MonthlyTurnOver from '../monthly-turn-over/monthly-turn-over';
import { Formik, Form, FormikErrors } from 'formik';
import { parseCurrencyAmount } from 'src/helpers/formatting';
import { DEVICE_NAMES } from 'src/features/order/tech-debt/product-details-extra';
import './fees-slider-modal.scss';

type InnerFormProps = {
  values: FeesSliderForm;
  setNoTurnoverAmount: React.Dispatch<React.SetStateAction<boolean>>;
  step: React.ReactNode;
  currentStepIndex: number;
  onClick: (values: FeesSliderForm) => void;
  buttonDisabled: (devices: IDevice[], values: FeesSliderForm) => boolean;
  devices: IDevice[];
};

const InnerForm = ({
  values,
  setNoTurnoverAmount,
  step,
  currentStepIndex,
  onClick,
  buttonDisabled,
  devices,
}: InnerFormProps) => {
  useEffect(() => {
    setNoTurnoverAmount(values.dontKnowTurnover);
  }, [values.dontKnowTurnover]);
  return (
    <Form>
      {step}

      <div className="fees-slider-button-container">
        <Button
          buttonStyle={DefaultButtonStyle()
            .hasPrimaryAction()
            .isMediumSize()
            .hasLightMode()
            .build()}
          buttonParams={{
            type: ButtonParamType.NormalButton,
            onClick: () => onClick(values),
          }}
          type="button"
          className="fees-slider-button"
          disabled={buttonDisabled(devices, values)}
        >
          {currentStepIndex === 0 ? 'Calculate your fees' : 'Back'}
        </Button>
      </div>
    </Form>
  );
};

interface FeesSliderModalProps {
  show: boolean;
  hide: () => void;
  selectedDevices: CatalogueItem[];
  feesService: IFeesService;
  catalogue: Catalogue;
  initialTurnoverAmount?: string;
}

interface FeesSliderForm {
  turnoverAmount: string;
  dontKnowTurnover: boolean;
}

const FeesSliderModal = ({
  show,
  hide,
  selectedDevices,
  feesService,
  catalogue,
  initialTurnoverAmount,
}: FeesSliderModalProps) => {
  const MINIMUM_TURNOVER = 0;
  const MAXIMUM_TURNOVER = 999999999.99;

  const [devices, setDevices] = useState<IDevice[]>(feeSliderDevices);
  const [noTurnoverAmount, setNoTurnoverAmount] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<FeesSliderForm>({
    turnoverAmount: '6000',
    dontKnowTurnover: false,
  });

  useEffect(() => {
    if (catalogue.products && catalogue.products.length > 0) {
      setDevices(
        feeSliderDevices.filter(
          (item) =>
            item.businessName === DEVICE_NAMES.VODAPAY_CHOP_CHOP ||
            catalogue.products.some((prod) => {
              return prod.device?.title.includes(item.businessName);
            }),
        ),
      );
    }
  }, [catalogue]);

  useEffect(() => {
    if (selectedDevices.length === 0) return;
    const updatedDevices = [...devices];

    selectedDevices.forEach((item) => {
      const deviceTitle = item.device.title;
      const index = devices.findIndex((value) => deviceTitle.includes(value.businessName));
      if (index >= 0) updatedDevices[index].active = true;
    });

    setDevices(updatedDevices);
  }, [selectedDevices]);

  function handleClick(index) {
    const updatedDevices = devices;
    updatedDevices[index].active = !updatedDevices[index].active;
    setDevices([...updatedDevices]);
  }

  function getDeviceNames(devices: IDevice[]) {
    return devices.filter((item) => item.active).map((item) => item.businessName);
  }

  const { step, currentStepIndex, next, back } = useMultiStep([
    <ChooseYourDevice
      key={0}
      devices={devices}
      onClick={handleClick}
      noTurnoverAmount={noTurnoverAmount}
    />,
    <MonthlyTurnOver
      key={1}
      feesService={feesService}
      selectedDeviceNames={getDeviceNames(devices)}
      defaultTurnover={
        formValues?.dontKnowTurnover ? 6000 : parseCurrencyAmount(formValues.turnoverAmount)
      }
    />,
  ]);

  function handleClose() {
    back();
    hide();
  }

  const validate = (values: FeesSliderForm): FormikErrors<FeesSliderForm> => {
    const errors: FormikErrors<FeesSliderForm> = {};
    const { turnoverAmount, dontKnowTurnover } = values;

    const estimatedTurnover = parseCurrencyAmount(turnoverAmount || '0');

    if (
      !dontKnowTurnover &&
      (estimatedTurnover === undefined ||
        estimatedTurnover < MINIMUM_TURNOVER ||
        estimatedTurnover >= MAXIMUM_TURNOVER)
    ) {
      errors.turnoverAmount = 'The amount entered is invalid.';
    }

    return errors;
  };

  const onClick = (values: FeesSliderForm) => {
    if (currentStepIndex === 1) {
      back();
      return;
    }
    setFormValues(values);
    if (!devices.some((item) => item.active) && Object.keys(validate(values)).length > 0) return;

    next();
  };

  const buttonDisabled = (devices: IDevice[], values: FeesSliderForm) => {
    if (devices.every((item) => !item.active)) return true;
    if (!values.dontKnowTurnover && Number(values?.turnoverAmount || '0') <= 0) return true;
    return false;
  };

  return (
    <Modal visible={show} size="custom-small" onClose={handleClose}>
      <div className="fees-slider-modal">
        <Formik<FeesSliderForm>
          initialValues={{
            dontKnowTurnover: false,
            turnoverAmount: initialTurnoverAmount ?? '',
          }}
          validate={validate}
          onSubmit={() => {}}
          validateOnChange
          validateOnBlur
          enableReinitialize
        >
          {({ values }) => (
            <InnerForm
              values={values}
              step={step}
              devices={devices}
              onClick={onClick}
              buttonDisabled={buttonDisabled}
              setNoTurnoverAmount={setNoTurnoverAmount}
              currentStepIndex={currentStepIndex}
            />
          )}
        </Formik>
      </div>
    </Modal>
  );
};

export default FeesSliderModal;
